import { get } from 'svelte/store';
import { format } from '../../services/i18n';
import { devices } from 'src/stores/lists/devices';
import { real_id } from 'src/helpers/interface_helpers';
import { capFirstChar } from '../../helpers/utilities';
import type { EventsLogData } from '../../types/requests/eventslog/events_log';
import { setSidePanelState } from '../Sidepanel/sidepanel-helpers';
import { getEventTableHeadings } from './event_log_helper';

export type EventTableHeading = {
	id: string;
	title?: string;
	sort?: boolean;
	desc?: boolean;
	icon?: string;
};

type EventTableHeadingsType = {
	[key: string]: EventTableHeading;
};

/**
 * Table headings for the redesigned Activity Log, including the Properties column.
 */
export const getEventTableHeadingsV2 = (): EventTableHeadingsType => {
	const sharedHeadings = getEventTableHeadings();
	const { info, ...headingsWithoutInfo } = sharedHeadings;

	return {
		...headingsWithoutInfo,
		properties: {
			id: 'properties',
			title: get(format)('wizard_step_device_prop', { default: 'Device properties' }),
			sort: false,
		},
		info,
	};
};

/**
 * Human-readable text for a property row inside the mobile details modal,
 * e.g. `Power: 570 W`. Falls back to the raw value when the key has no
 * translation or already carries its own descriptive label.
 */
export const getEventPropertyLabel = (property: { name?: string; value?: unknown }): string => {
	const value = property?.value == null ? '' : String(property.value);
	const name = property?.name;

	if (!name) return value;

	const isTokenKey = /^[a-z0-9_]+$/i.test(name);

	if (!isTokenKey) return value || name;

	const label = get(format)(name, { default: capFirstChar(name.replace(/_/g, ' ')) });

	return value ? `${label}: ${value}` : label;
};

/**
 * Opens the side panel of the entity the event belongs to, either the
 * scene / alarm that triggered it or the device that reported it.
 */
export const openEventSidePanel = (event: EventsLogData) => {
	if (event.category === 'scene' || event.category === 'alarm') {
		setSidePanelState({ type: event.category, id: event.sceneId });
	} else {
		const allDevices = get(devices);
		const device = allDevices[event.id] || allDevices[real_id(event.id)];

		if (!device?.id) return;

		setSidePanelState({ type: 'device', id: device.id });
	}

	setSidePanelState({ open: true });
};
